import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { AuthService } from '../services/auth';
import { useAuthStore } from '../store/authStore';
import PublicHeader from '../components/PublicHeader';
import { Foundation } from '@mui/icons-material';
import PublicFooter from '../components/PublicFooter';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  companyName: yup.string().required('Company Name is required'),
  ice: yup.string().optional(),
  firstName: yup.string().required('First Name is required'),
  lastName: yup.string().required('Last Name is required'),
});

type FormData = yup.InferType<typeof schema>;

const PromoteurSignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { setUser } = useAuthStore();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError(null);
    
    try {
      const user = await AuthService.signUpWithEmail(data.email, data.password, {
        firstName: data.firstName,
        lastName: data.lastName,
        company: data.companyName,
        ice: data.ice,
      });

      // Force role as Promoteur locally
      setUser({ ...user, role: 'promoteur' as any }); 
      
      // In production, update the user profile in Firestore directly here
      navigate('/promoteur'); 
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <PublicHeader />
      <Container maxWidth="sm" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 12 }}>
        <Card sx={{ borderRadius: 4, boxShadow: '0 24px 48px rgba(0,0,0,0.1)' }}>
          <Box sx={{ p: 4, bgcolor: 'secondary.main', color: 'white', textAlign: 'center' }}>
            <Foundation sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: '"Playfair Display", serif' }}>
              Promoteur Portal
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              List entire Chantiers and manage unit inventory
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <form onSubmit={handleSubmit(onSubmit)}>
              <TextField
                {...register('companyName')}
                fullWidth
                label="Development Company Name"
                margin="normal"
                error={!!errors.companyName}
                helperText={errors.companyName?.message}
              />
              <TextField
                {...register('ice')}
                fullWidth
                label="ICE (Identifiant Commun de l'Entreprise)"
                margin="normal"
                error={!!errors.ice}
                helperText={errors.ice?.message}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  {...register('firstName')}
                  fullWidth
                  label="Contact First Name"
                  margin="normal"
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                />
                <TextField
                  {...register('lastName')}
                  fullWidth
                  label="Contact Last Name"
                  margin="normal"
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                />
              </Box>
              <TextField
                {...register('email')}
                fullWidth
                label="Corporate Email"
                type="email"
                margin="normal"
                error={!!errors.email}
                helperText={errors.email?.message}
              />
              <TextField
                {...register('password')}
                fullWidth
                label="Password"
                type="password"
                margin="normal"
                error={!!errors.password}
                helperText={errors.password?.message}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="secondary"
                size="large"
                disabled={loading}
                sx={{ mt: 4, mb: 2, py: 1.5, fontSize: '1.1rem', fontWeight: 600, boxShadow: 'none' }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Promoteur Account'}
              </Button>
              <Button
                fullWidth
                variant="text"
                color="secondary"
                onClick={() => navigate('/login')}
                sx={{ textTransform: 'none' }}
              >
                Already registered? Sign In
              </Button>
            </form>
          </CardContent>
        </Card>
      </Container>
      <PublicFooter />
    </Box>
  );
};

export default PromoteurSignupPage;
